import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Mail, Loader2, Paperclip, Eye, Send, Pencil, X, Calendar as CalendarIcon } from 'lucide-react';

/**
 * Reusable modal that fetches a preview of an outgoing email and lets the
 * user review it (subject, to, cc, HTML body, PDF attachment) before sending.
 *
 * Props:
 *  - open, onClose
 *  - title              (string) e.g. "Enviar EDP N° 214"
 *  - fetchPreview       (async fn -> {to, cc, subject, body_html, pdf_url?, pdf_filename?})
 *  - sendEmail          (async fn({to, cc, subject, body_html}) -> void)  called on "Enviar ahora"
 *  - defaultTo          (optional pre-filled To when preview cannot infer one)
 *  - defaultCc          (optional pre-filled CC)
 *  - allowSchedule      (bool) if true, shows "Programar envío" button
 *  - scheduleEmail      (async fn({to, cc, fecha_programada}) -> void)
 *  - showRawHtmlToggle  (bool default true)
 */
export default function EmailPreviewModal({
    open,
    onClose,
    title,
    fetchPreview,
    sendEmail,
    defaultTo = '',
    defaultCc = '',
    allowSchedule = false,
    scheduleEmail,
    apiBaseUrl,
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [to, setTo] = useState(defaultTo);
    const [cc, setCc] = useState(defaultCc);
    const [subject, setSubject] = useState('');
    const [bodyHtml, setBodyHtml] = useState('');
    const [pdfUrl, setPdfUrl] = useState('');
    const [pdfFilename, setPdfFilename] = useState('');
    const [bcc, setBcc] = useState('');
    const [editingBody, setEditingBody] = useState(false);
    const [sending, setSending] = useState(false);
    const [scheduling, setScheduling] = useState(false);
    const [scheduleDate, setScheduleDate] = useState('');
    const [showScheduleForm, setShowScheduleForm] = useState(false);

    // Load preview when opening (or when user manually retries)
    const runPreview = async (overrides = {}) => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchPreview({
                to: overrides.to || to || defaultTo || undefined,
                cc: overrides.cc ?? cc ?? defaultCc ?? undefined,
            });
            setTo(data.to || overrides.to || to || defaultTo || '');
            setCc(data.cc || overrides.cc || cc || defaultCc || '');
            setSubject(data.subject || '');
            setBodyHtml(data.body_html || '');
            setPdfUrl(data.pdf_url || '');
            setPdfFilename(data.pdf_filename || '');
            setBcc(data.bcc || '');
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || 'Error cargando preview';
            setError(typeof msg === 'string' ? msg : 'Error cargando preview');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        // Reset local state each time the modal opens
        setTo(defaultTo || '');
        setCc(defaultCc || '');
        setSubject('');
        setBodyHtml('');
        setPdfUrl('');
        setPdfFilename('');
        setBcc('');
        setEditingBody(false);
        setShowScheduleForm(false);
        setScheduleDate('');
        runPreview({ to: defaultTo, cc: defaultCc });
    }, [open]);

    // Reload preview when user edits To/CC/subject and expects updated content?
    // We keep this manual (button) to avoid backend hammering.

    const handleSend = async () => {
        if (!to.trim()) return;
        setSending(true);
        try {
            await sendEmail({ to: to.trim(), cc: cc.trim() || null, subject, body_html: bodyHtml });
            onClose();
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || 'Error enviando correo');
        } finally {
            setSending(false);
        }
    };

    const handleSchedule = async () => {
        if (!scheduleDate) return;
        setScheduling(true);
        try {
            await scheduleEmail({
                to: to.trim() || null,
                cc: cc.trim() || null,
                fecha_programada: scheduleDate,
            });
            onClose();
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || 'Error programando envío');
        } finally {
            setScheduling(false);
        }
    };

    // Compute a full PDF URL (backend returns /api/uploads/...)
    const pdfHref = pdfUrl
        ? (pdfUrl.startsWith('http') ? pdfUrl : `${apiBaseUrl}${pdfUrl}`)
        : null;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="email-preview-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-teal-700" />
                        {title || 'Vista previa del correo'}
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                        <Loader2 className="w-8 h-8 animate-spin text-teal-600 mb-3" />
                        <p className="text-sm">Generando vista previa…</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {error && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start justify-between gap-3">
                                <span>{error}</span>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => runPreview()}
                                    disabled={!to.trim()}
                                    className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-100 shrink-0"
                                    data-testid="email-preview-retry-btn"
                                >
                                    Reintentar
                                </Button>
                            </div>
                        )}

                        {/* Header fields */}
                        <div className="grid gap-3 border rounded-lg p-4 bg-slate-50">
                            <div className="grid grid-cols-[80px_1fr] items-center gap-2">
                                <Label className="text-xs text-slate-500">Para:</Label>
                                <Input
                                    data-testid="email-preview-to"
                                    value={to}
                                    onChange={(e) => setTo(e.target.value)}
                                    className="h-8 text-sm bg-white"
                                />
                            </div>
                            <div className="grid grid-cols-[80px_1fr] items-center gap-2">
                                <Label className="text-xs text-slate-500">CC:</Label>
                                <Input
                                    data-testid="email-preview-cc"
                                    value={cc}
                                    onChange={(e) => setCc(e.target.value)}
                                    placeholder="Sin copia (opcional, separar por coma)"
                                    className="h-8 text-sm bg-white"
                                />
                            </div>
                            {bcc && (
                                <div className="grid grid-cols-[80px_1fr] items-center gap-2">
                                    <Label className="text-xs text-slate-500">CCO:</Label>
                                    <div className="text-xs text-slate-500 bg-white border border-dashed rounded px-2 py-1.5" data-testid="email-preview-bcc">
                                        {bcc}
                                        <span className="ml-2 text-slate-400 italic">(oculto para el destinatario — puedes cambiarlo en Configuración)</span>
                                    </div>
                                </div>
                            )}
                            <div className="grid grid-cols-[80px_1fr] items-center gap-2">
                                <Label className="text-xs text-slate-500">Asunto:</Label>
                                <Input
                                    data-testid="email-preview-subject"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    className="h-8 text-sm bg-white"
                                />
                            </div>

                            {pdfFilename && (
                                <div className="grid grid-cols-[80px_1fr] items-center gap-2">
                                    <Label className="text-xs text-slate-500">Adjunto:</Label>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Paperclip className="w-4 h-4 text-slate-400" />
                                        <span className="text-slate-700 truncate">{pdfFilename}</span>
                                        {pdfHref && (
                                            <a
                                                href={pdfHref}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-teal-700 hover:underline text-xs flex items-center gap-1"
                                                data-testid="email-preview-pdf-link"
                                            >
                                                <Eye className="w-3 h-3" /> Ver PDF
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Body preview */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <Label className="text-xs text-slate-500 uppercase">Cuerpo del correo</Label>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingBody(!editingBody)}
                                    className="h-7 text-xs"
                                    data-testid="email-preview-toggle-edit"
                                >
                                    {editingBody
                                        ? (<><Eye className="w-3 h-3 mr-1" /> Vista previa</>)
                                        : (<><Pencil className="w-3 h-3 mr-1" /> Editar HTML</>)}
                                </Button>
                            </div>
                            {editingBody ? (
                                <Textarea
                                    data-testid="email-preview-body-textarea"
                                    value={bodyHtml}
                                    onChange={(e) => setBodyHtml(e.target.value)}
                                    className="min-h-[300px] font-mono text-xs"
                                />
                            ) : (
                                <div
                                    className="border rounded-lg overflow-hidden bg-white"
                                    data-testid="email-preview-body-frame"
                                >
                                    <iframe
                                        title="Vista previa del correo"
                                        srcDoc={bodyHtml}
                                        sandbox=""
                                        className="w-full h-[420px] border-0"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Schedule form (collapsible) */}
                        {allowSchedule && showScheduleForm && (
                            <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-2" data-testid="email-schedule-form">
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                                        <CalendarIcon className="w-4 h-4" /> Programar envío automático
                                    </Label>
                                    <Button size="sm" variant="ghost" onClick={() => setShowScheduleForm(false)} className="h-7 w-7 p-0">
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                                <p className="text-xs text-amber-800">
                                    Se enviará automáticamente al destinatario en la fecha y hora indicadas.
                                </p>
                                <Input
                                    data-testid="email-schedule-date"
                                    type="datetime-local"
                                    value={scheduleDate}
                                    onChange={(e) => setScheduleDate(e.target.value)}
                                    className="bg-white text-sm"
                                />
                                <Button
                                    onClick={handleSchedule}
                                    disabled={!scheduleDate || scheduling}
                                    className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                                    data-testid="email-schedule-submit-btn"
                                >
                                    {scheduling
                                        ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Programando…</>)
                                        : (<><CalendarIcon className="w-4 h-4 mr-2" /> Programar</>)}
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter className="gap-2 flex-wrap">
                    {allowSchedule && !showScheduleForm && !loading && (
                        <Button
                            variant="outline"
                            onClick={() => setShowScheduleForm(true)}
                            className="border-amber-400 text-amber-700 hover:bg-amber-50"
                            data-testid="email-schedule-open-btn"
                        >
                            <CalendarIcon className="w-4 h-4 mr-2" /> Programar envío
                        </Button>
                    )}
                    <div className="flex-1" />
                    <Button variant="outline" onClick={onClose} data-testid="email-preview-cancel-btn">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={sending || loading || !to.trim() || !subject.trim()}
                        className="bg-teal-700 hover:bg-teal-800 text-white"
                        data-testid="email-preview-send-btn"
                    >
                        {sending
                            ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando…</>)
                            : (<><Send className="w-4 h-4 mr-2" /> Enviar ahora</>)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
